import { calculateTechnicalScore, getLatestIndicators } from "./indicators.js";
import { SAFE_ACTIONS } from "../utils/constants.js";
import { clamp } from "../utils/formatters.js";

export function mapScoreToAction(score) {
  if (score >= 75) return SAFE_ACTIONS.BUY_CHECK;
  if (score >= 60) return SAFE_ACTIONS.WATCH;
  if (score >= 45) return SAFE_ACTIONS.HOLD;
  if (score >= 30) return SAFE_ACTIONS.REDUCE;
  return SAFE_ACTIONS.SELL_CHECK;
}

export function calculateMacroScore(macro = {}) {
  let score = 50;
  const reasons = [];
  const warnings = [];

  const usd = Number(macro.usdIndex);
  const realYield = Number(macro.realYield);
  const tenYear = Number(macro.tenYearYield);
  const fedRate = Number(macro.fedRate);
  const inflation = Number(macro.inflation);
  const sp500 = Number(macro.sp500);

  if (Number.isFinite(usd)) {
    if (usd < 102) {
      score += 10;
      reasons.push("Индекс доллара США относительно слабый");
    } else if (usd > 105) {
      score -= 10;
      warnings.push("Укрепление доллара США может давить на золото");
    }
  }

  if (Number.isFinite(realYield)) {
    if (realYield < 1.7) {
      score += 12;
      reasons.push("Реальные доходности остаются умеренными");
    } else if (realYield > 2.1) {
      score -= 12;
      warnings.push("Высокие реальные доходности могут давить на золото");
    }
  }

  if (Number.isFinite(tenYear) && tenYear > 4.6) {
    score -= 5;
    warnings.push("Доходность 10-летних облигаций США находится высоко");
  }

  if (Number.isFinite(fedRate) && Number.isFinite(inflation)) {
    if (inflation > 3 && fedRate >= 4) {
      score += 5;
      reasons.push("Устойчивая инфляция остается важной темой для золота");
    }
    if (inflation < 2.5 && fedRate > 4) {
      score -= 5;
      warnings.push("Снижение инфляции может ослабить ожидания снижения ставок");
    }
  }

  if (Number.isFinite(sp500) && sp500 > 5200) {
    score -= 3;
    warnings.push("Risk-on среда может снижать спрос на защитные активы");
  }

  if (Array.isArray(macro.missingFields) && macro.missingFields.length) {
    score -= Math.min(8, macro.missingFields.length * 2);
    warnings.push("Часть макро-данных отсутствует и заменена fallback-значениями");
  }

  return {
    score: Math.round(clamp(score)),
    reasons,
    warnings,
  };
}

export function calculateRiskScore({ indicators = {}, calendar = [], apiStatus = {} } = {}) {
  let score = 75;
  const warnings = [];
  const rsi = Number(indicators.rsi14);
  const atr = Number(indicators.atr14);
  const price = Number(indicators.price);

  if (Number.isFinite(rsi) && rsi > 75) {
    score -= 18;
    warnings.push("RSI сильно перекуплен");
  }
  if (Number.isFinite(rsi) && rsi < 30) {
    score -= 10;
    warnings.push("RSI перепродан, возможна обратная реакция");
  }
  if (Number.isFinite(atr) && Number.isFinite(price) && price > 0 && (atr / price) * 100 > 1.6) {
    score -= 12;
    warnings.push("ATR указывает на повышенную волатильность");
  }

  const zurichDayFormatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Zurich",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const todayZurich = zurichDayFormatter.format(new Date());

  const highImpactToday = (Array.isArray(calendar) ? calendar : []).some((event) => {
    const eventDate = new Date(event?.time);
    if (Number.isNaN(eventDate.getTime())) return false;
    const eventDay = zurichDayFormatter.format(eventDate);
    return eventDay === todayZurich && ["very high", "high"].includes(event.impact);
  });

  if (highImpactToday) {
    score -= 16;
    warnings.push("Сегодня есть событие с высоким влиянием");
  }

  if (apiStatus.fallbackActive) {
    score -= 10;
    warnings.push("Активны fallback-данные");
  }
  if (apiStatus.geminiFallback) {
    score -= 6;
    warnings.push("Активен keyword fallback вместо Gemini");
  }

  return {
    score: Math.round(clamp(score)),
    warnings,
    highImpactToday,
  };
}

export function calculateConfidence({ technicalScore, macroScore, newsScore, riskScore, apiStatus = {} }) {
  if (apiStatus.fallbackActive || apiStatus.geminiFallback || apiStatus.missingFields?.length || riskScore < 50) {
    return "low";
  }

  const values = [technicalScore, macroScore, newsScore].map(Number);
  const allBullish = values.every((value) => value >= 60);
  const allBearish = values.every((value) => value <= 40);
  const noErrors = !apiStatus.apiError;

  if ((allBullish || allBearish) && noErrors && riskScore >= 60) return "high";
  return "medium";
}

export function calculateSignal({
  price,
  history = [],
  macro = {},
  newsAnalysis = {},
  calendar = [],
  apiStatus = {},
} = {}) {
  const indicators = getLatestIndicators(history);
  const effectiveIndicators = {
    ...indicators,
    price: Number(price?.price ?? indicators.price),
  };
  const technical = calculateTechnicalScore(effectiveIndicators);
  const macroResult = calculateMacroScore(macro);
  const newsScore = Math.round(clamp(Number(newsAnalysis?.score ?? 50)));
  const risk = calculateRiskScore({ indicators: effectiveIndicators, calendar, apiStatus });
  const score = Math.round(
    technical.score * 0.35 + macroResult.score * 0.25 + newsScore * 0.25 + risk.score * 0.15,
  );

  const warnings = [
    ...technical.warnings,
    ...macroResult.warnings,
    ...(Array.isArray(newsAnalysis?.riskWarnings) ? newsAnalysis.riskWarnings : []),
    ...risk.warnings,
  ].slice(0, 8);

  const reasons = [
    ...technical.reasons,
    ...macroResult.reasons,
    ...(Array.isArray(newsAnalysis?.keyDrivers) ? newsAnalysis.keyDrivers.map((driver) => `Новости: ${driver}`) : []),
  ].slice(0, 8);

  return {
    score,
    action: mapScoreToAction(score),
    confidence: calculateConfidence({
      technicalScore: technical.score,
      macroScore: macroResult.score,
      newsScore,
      riskScore: risk.score,
      apiStatus,
    }),
    technicalScore: technical.score,
    macroScore: macroResult.score,
    newsScore,
    riskScore: risk.score,
    reasons: reasons.length ? reasons : ["Данные смешанные, поэтому это только помощник анализа."],
    warnings,
    timestamp: new Date().toISOString(),
    indicators: effectiveIndicators,
  };
}
